import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { CUSTOMERS } from "@/hooks";
import type { QueryGetCustomersArgs } from "@/types/graphql";

/**
 * Custom hook to fetch companies based on user and customer IDs.
 * @param {object} variables - The variables for the query.
 * @param {string} [variables.userId] - The ID of the user.
 * @param {string} [variables.customerId] - The ID of the customer.
 * @param {string} [variables.customerType] - The Type of the customer.
 * @returns {{customers: Array<object>, loading: boolean}} - The list of customers and loading state.
 */
export function useCompanies(variables: QueryGetCustomersArgs = {}) {
  const userId = variables?.userId || null;
  const customerId = variables?.customerId || null;
  let tenantId = null;
  const customerType = variables?.customerType || null;
  if (variables?.tenantId && variables?.tenantId !== "all") {
    tenantId = variables?.tenantId;
  }

  const queryVariables = useMemo(() => {
    return {
      ...(userId !== null && { userId }),
      ...(customerId !== null && { customerId }),
      ...(tenantId !== null && { tenantId }),
      ...(customerType !== null && { customerType }),
    };
  }, [userId, customerId, tenantId, customerType]);

  const { data, loading } = useQuery<any>(CUSTOMERS, {
    variables: queryVariables,
  });

  const customers = useMemo(() => {
    return data?.getCustomers || [];
  }, [data?.getCustomers]);

  return {
    customers,
    loading,
  };
}
